import React, { useState, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

import i18n from 'i18n-js';
import Can from 'components/Can';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ChangeLocationsModal } from 'components/Modals';

const listItemTextSlotProps = {
  primary: {
    sx: {
      fontSize: '13px',
      fontWeight: '600',
      wordBreak: 'break-word',
      color: 'text.subtitle',
      marginRight: '3rem',
    },
  },
  secondary: {
    sx: {
      color: 'common.lightNavy',
      fontSize: '13px',
      wordBreak: 'break-word',
    },
  },
};

const LocationsWidget = ({
  locations,
  pagination,
  params,
  onDelete,
  steps,
  loading,
  currentGroup,
  handleChangePage,
}) => {
  const { orgType } = useContext(UserContext);
  const [open, setOpen] = useState(false);

  const handleLocationsModalOpen = () => {
    setOpen((prevState) => !prevState);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('locations.edit'),
      can: { name: 'vehicle_group', action: 'update' },
      onClick: handleLocationsModalOpen,
    },
  ];

  const onDeleteClick = (item) => {
    const locationId = item.id;
    onDelete(locationId);
  };

  const getType = () => {
    if (orgType === 'msServiceOrg' || orgType === 'plainOrg') {
      return 'customers';
    }
    return 'service';
  };

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      data-cy="locations-widget-card"
      title={i18n.t('locations.title')}
    >
      {loading ? (
        <Box justifyContent="center" alignItems="center" display="flex">
          <Loading />
        </Box>
      ) : (
        <>
          <Box>
            {locations?.length > 0 ? (
              <>
                <List
                  sx={{ maxHeight: 400, overflowY: 'auto' }}
                  data-cy="locations-widget-card-list"
                >
                  {locations?.map((val, index) => (
                    <Fragment key={index}>
                      <ListItem>
                        <ListItemText slotProps={listItemTextSlotProps} primary={`${val.name}`} />
                        <ListItemSecondaryAction
                          sx={{
                            display: 'flex',
                            minWidth: '50px',
                            wordBreak: 'break-all',
                            textAlign: 'end',
                          }}
                        >
                          <Can I="update" a="vehicle_group">
                            <Box fontSize={13}>
                              <IconButton onClick={handleLocationsModalOpen}>
                                <EditIcon
                                  style={{ color: '#8E8E93', fontSize: 16, cursor: 'pointer' }}
                                />
                              </IconButton>
                            </Box>
                          </Can>
                          <Can I="update" a="vehicle_group">
                            <Box fontSize={13}>
                              <IconButton onClick={() => onDeleteClick(val)}>
                                <DeleteIcon
                                  style={{ color: '#8E8E93', fontSize: 16, cursor: 'pointer' }}
                                />
                              </IconButton>
                            </Box>
                          </Can>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </Fragment>
                  ))}
                </List>
                <Paginator
                  component="div"
                  limit={params.limit}
                  pagination={pagination}
                  onPageChange={handleChangePage}
                />
              </>
            ) : (
              <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                <Box fontSize={14} component="span" lineHeight={1}>
                  {i18n.t('vehicle_groups.no_locations')}
                </Box>
              </Typography>
            )}
          </Box>
        </>
      )}
      {open && (
        <ChangeLocationsModal
          open={open}
          steps={steps}
          type={getType()}
          currentGroup={currentGroup}
          handleClick={handleLocationsModalOpen}
        />
      )}
    </Card>
  );
};

LocationsWidget.propTypes = {
  locations: PropTypes.array,
  pagination: PropTypes.object,
  params: PropTypes.object,
  onDelete: PropTypes.func,
  steps: PropTypes.array,
  loading: PropTypes.bool,
  currentGroup: PropTypes.object,
  handleChangePage: PropTypes.func,
};

export default LocationsWidget;
